import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImageSquare, FilmSlate, MagicWand, CircleNotch, DownloadSimple, Trash, UploadSimple, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, BACKEND } from "@/lib/api";

const IMG_IDEAS = [
  "A serene peach-toned mountain landscape at sunrise, minimalist",
  "A cute mascot robot for a tax app, pastel colors, sticker style",
  "Product photo of a ceramic coffee mug on marble, studio light",
];
const VID_IDEAS = [
  "A hot air balloon drifting over pastel hills at dawn",
  "Neon city street in the rain, cinematic, reflections",
  "A seed growing into a glowing plant, time-lapse feel",
];

const TABS = [
  { id: "image", label: "Image", icon: ImageSquare },
  { id: "video", label: "Video", icon: FilmSlate },
  { id: "edit", label: "Edit", icon: MagicWand },
];

export default function MediaStudio() {
  const [tab, setTab] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [gallery, setGallery] = useState([]);

  // video
  const [aspect, setAspect] = useState("16:9");
  const [videos, setVideos] = useState([]);
  const [vidStatus, setVidStatus] = useState("");
  const [vidLength, setVidLength] = useState("short");
  const [durationMin, setDurationMin] = useState(5);
  const [narration, setNarration] = useState(true);
  const [voice, setVoice] = useState("nova");
  // reference attachments (docs / images) shared by image + video generation
  const [attachments, setAttachments] = useState([]);
  const [uploadingRef, setUploadingRef] = useState(false);
  const refInput = useRef(null);
  const mounted = useRef(true);

  // edit
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const loadGallery = useCallback(async () => { try { const r = await api.get("/media"); setGallery(r.data); } catch (e) { console.error(e); } }, []);
  const loadVideos = useCallback(async () => { try { const r = await api.get("/media/videos"); setVideos(r.data); } catch (e) { console.error(e); } }, []);

  useEffect(() => {
    mounted.current = true;
    loadGallery();
    loadVideos();
    return () => { mounted.current = false; };
  }, [loadGallery, loadVideos]);

  const onRefUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploadingRef(true);
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, { upload_id: up.data.upload_id, filename: up.data.filename, kind: up.data.kind }]);
      } catch { toast.error(`Failed to upload ${f.name}`); }
    }
    setUploadingRef(false); if (refInput.current) refInput.current.value = "";
  };
  const removeRef = (id) => setAttachments((a) => a.filter((x) => x.upload_id !== id));

  const generateImage = async () => {
    const p = prompt.trim(); if (!p || loading) return;
    setLoading(true); setCurrent(null);
    try {
      const r = await api.post("/media/generate", { prompt: p, attachments: attachments.map((a) => a.upload_id) });
      setCurrent({ type: "image", ...r.data }); toast.success("Image generated"); loadGallery();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const generateVideo = async () => {
    const p = prompt.trim(); if (!p || loading) return;
    const isLong = vidLength === "long";
    setLoading(true); setCurrent(null); setVidStatus(isLong ? "Writing storyboard…" : "Generating cinematic frames…");
    try {
      const body = { prompt: p, aspect, frames: 4, attachments: attachments.map((a) => a.upload_id) };
      if (isLong) { body.duration_min = durationMin; body.narration = narration; body.voice = voice; }
      const r = await api.post("/media/video", body);
      const id = r.data.video_id;
      const started = Date.now();
      const limit = isLong ? 1200000 : 240000; // long videos can take several minutes
      const poll = async () => {
        if (!mounted.current) return;
        try {
          const res = await api.get(`/media/video/${id}`);
          const v = res.data;
          if (v.status === "done") { setCurrent({ type: "video", ...v }); setLoading(false); setVidStatus(""); toast.success("Video ready"); loadVideos(); return; }
          if (v.status === "error") { setLoading(false); setVidStatus(""); toast.error("Video failed: " + (v.error || "")); return; }
          if (Date.now() - started > limit) { setLoading(false); setVidStatus(""); toast.error("Video timed out"); return; }
          setVidStatus(v.phase ? `Rendering — ${v.phase}${typeof v.progress === "number" ? ` (${v.progress}%)` : ""}` : "Rendering video…");
          setTimeout(poll, 3000);
        } catch { setTimeout(poll, 3500); }
      };
      setTimeout(poll, 3000);
    } catch (e) { setLoading(false); setVidStatus(""); toast.error(e?.response?.data?.detail || "Video failed"); }
  };

  const onEditFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setEditFile(f); setEditPreview(URL.createObjectURL(f));
  };

  const runEdit = async () => {
    if (!editFile) { toast.error("Upload an image first"); return; }
    const p = prompt.trim(); if (!p) { toast.error("Describe the edit"); return; }
    setLoading(true); setCurrent(null);
    try {
      const fd = new FormData(); fd.append("file", editFile);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.post("/media/edit", { prompt: p, upload_id: up.data.upload_id });
      setCurrent({ type: "image", ...r.data }); toast.success("Image edited"); loadGallery();
    } catch (e) { toast.error(e?.response?.data?.detail || "Edit failed"); }
    finally { setLoading(false); }
  };

  const removeImg = async (id) => { try { await api.delete(`/media/${id}`); setGallery((g) => g.filter((x) => x.media_id !== id)); } catch (e) { console.error(e); } };
  const removeVid = async (id) => { try { await api.delete(`/media/video/${id}`); setVideos((v) => v.filter((x) => x.video_id !== id)); } catch (e) { console.error(e); } };
  const download = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = `${name}`; a.target = "_blank"; a.click(); };

  const ideas = tab === "video" ? VID_IDEAS : IMG_IDEAS;

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="h-16 flex items-center -mt-4 mb-2" />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#FFF4F0] flex items-center justify-center">
          <ImageSquare size={24} weight="duotone" className="text-[#F29A86]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Media Studio</h1>
          <p className="text-[#594F4D] text-sm">Generate & edit images and short or long-form (up to 10-min) AI videos</p>
        </div>
        <div className="ml-auto flex items-center gap-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg p-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`media-tab-${t.id}`} onClick={() => { setTab(t.id); setCurrent(null); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${tab === t.id ? "bg-[#FFB8A5] text-[#2A2625]" : "text-[#594F4D] hover:text-[#2A2625]"}`}>
              <t.icon size={14} weight="bold" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-6 mt-6">
        {tab === "edit" && (
          <div className="mb-4">
            <label className="text-sm font-medium">Source image</label>
            <div className="mt-2 flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer border border-dashed border-[#2A2625]/25 rounded-xl px-4 py-3 hover:bg-[#2A2625]/5 transition-colors" data-testid="edit-upload">
                <UploadSimple size={18} /> <span className="text-sm">{editFile ? "Change image" : "Upload image"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onEditFile} />
              </label>
              {editPreview && <img src={editPreview} alt="src" className="w-16 h-16 rounded-lg object-cover border border-[#2A2625]/10" />}
            </div>
          </div>
        )}
        <label className="text-sm font-medium">{tab === "edit" ? "Describe the edit" : tab === "video" ? "Describe the video" : "Describe the image"}</label>
        <textarea data-testid="media-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder={tab === "video" ? "e.g. A hot air balloon over pastel hills at dawn" : tab === "edit" ? "e.g. Make it night time with warm lights" : "e.g. A cozy reading nook with warm peach light"}
          className="mt-2 w-full resize-none bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
        <div className="flex flex-wrap gap-2 mt-3">
          {ideas.map((s) => <button key={s} onClick={() => setPrompt(s)} className="text-xs px-3 py-1.5 rounded-full bg-[#FCFAF8] border border-[#2A2625]/10 text-[#594F4D] hover:bg-[#2A2625]/5 transition-colors">{s}</button>)}
        </div>

        {tab !== "edit" && (
          <div className="mt-4 border-t border-[#2A2625]/8 pt-4">
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={refInput} type="file" multiple className="hidden" onChange={onRefUpload} accept="image/*,.pdf,.txt,.md,.csv,.json,.docx,.xlsx" />
              <button data-testid="media-attach-btn" onClick={() => refInput.current?.click()} disabled={uploadingRef}
                className="flex items-center gap-2 text-sm border border-dashed border-[#2A2625]/25 rounded-xl px-4 py-2 hover:bg-[#2A2625]/5 transition-colors">
                {uploadingRef ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} Attach reference docs / images
              </button>
              <span className="text-xs text-[#8C8381]">Guides the {tab === "video" ? "storyboard & scenes" : "generated image"} (style, brand, content)</span>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="media-attachments">
                {attachments.map((a) => (
                  <span key={a.upload_id} className="flex items-center gap-1.5 text-xs bg-[#FFF4F0] border border-[#2A2625]/10 rounded-full pl-3 pr-2 py-1.5">
                    {a.kind === "image" ? <ImageSquare size={13} /> : <UploadSimple size={13} />} <span className="max-w-[140px] truncate">{a.filename}</span>
                    <button onClick={() => removeRef(a.upload_id)} className="text-[#8C8381] hover:text-[#E09494]"><Trash size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "video" && (
          <div className="mt-4 grid gap-3 bg-[#FCFAF8] border border-[#2A2625]/10 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-[#594F4D]">Type</span>
              <div className="flex bg-white border border-[#2A2625]/15 rounded-lg p-0.5">
                <button data-testid="vid-length-short" onClick={() => setVidLength("short")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${vidLength === "short" ? "bg-[#FFB8A5] text-[#2A2625]" : "text-[#594F4D]"}`}>Short clip (~10s)</button>
                <button data-testid="vid-length-long" onClick={() => setVidLength("long")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${vidLength === "long" ? "bg-[#FFB8A5] text-[#2A2625]" : "text-[#594F4D]"}`}>Long form (1–10 min)</button>
              </div>
            </div>
            {vidLength === "long" && (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#594F4D] w-20">Duration</span>
                  <input data-testid="vid-duration" type="range" min="1" max="10" value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))} className="flex-1 accent-[#F29A86]" />
                  <span className="text-sm font-semibold w-14 text-right">{durationMin} min</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs font-medium text-[#594F4D] cursor-pointer">
                    <input data-testid="vid-narration" type="checkbox" checked={narration} onChange={(e) => setNarration(e.target.checked)} className="accent-[#F29A86] w-4 h-4" /> AI voice narration
                  </label>
                  {narration && (
                    <select data-testid="vid-voice" value={voice} onChange={(e) => setVoice(e.target.value)} className="bg-white border border-[#2A2625]/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
                      {["nova", "alloy", "echo", "fable", "onyx", "shimmer", "sage", "coral"].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4">
          {tab === "video" && (
            <select data-testid="video-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)} className="bg-[#FCFAF8] border border-[#2A2625]/15 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]">
              <option value="16:9">16:9 Landscape</option>
              <option value="9:16">9:16 Portrait</option>
            </select>
          )}
          <button data-testid="media-generate-btn" onClick={tab === "video" ? generateVideo : tab === "edit" ? runEdit : generateImage}
            disabled={loading || !prompt.trim()} className="ml-auto flex items-center gap-2 bg-[#FFB8A5] hover:bg-[#FFCBBF] disabled:opacity-40 text-[#2A2625] font-medium rounded-full px-6 py-2.5 transition-colors">
            {loading ? <><CircleNotch size={18} className="animate-spin" /> {tab === "video" ? "Rendering…" : "Working…"}</> : <><MagicWand size={18} weight="fill" /> {tab === "edit" ? "Apply edit" : "Generate"}</>}
          </button>
        </div>
        {tab === "video" && <p className="text-xs text-[#8C8381] mt-2">{vidLength === "long" ? `Long-form video = AI storyboard → per-scene AI images with Ken Burns motion${narration ? " + AI voice narration" : ""} + captions, stitched into a ${durationMin}-min film. Takes a few minutes.` : "Short AI video = AI-generated frames stitched into a cinematic clip. Takes ~1 minute."}</p>}
      </div>

      {/* Current result */}
      {(loading || current) && (
        <div className="mt-6 bg-white border border-[#2A2625]/10 rounded-2xl p-6">
          {loading && (
            <div className="aspect-video rounded-xl bg-[#FCFAF8] border border-[#2A2625]/10 flex items-center justify-center">
              <div className="text-center"><CircleNotch size={36} className="animate-spin text-[#F29A86] mx-auto" /><p className="text-sm text-[#8C8381] mt-3">{vidStatus || "Working…"}</p></div>
            </div>
          )}
          {!loading && current?.type === "image" && (
            <div className="flex flex-col md:flex-row gap-5">
              <img data-testid="media-current-image" src={`${BACKEND}${current.url}`} alt={current.prompt} className="w-full md:w-2/3 rounded-xl border border-[#2A2625]/10 object-cover" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-[#8C8381] font-medium">Prompt</p>
                <p className="text-sm mt-1">{current.prompt}</p>
                <button onClick={() => download(`${BACKEND}${current.url}`, current.media_id + ".png")} className="mt-4 flex items-center gap-2 text-sm border border-[#2A2625]/15 hover:bg-[#2A2625]/5 rounded-full px-4 py-2 transition-colors"><DownloadSimple size={16} /> Download</button>
              </div>
            </div>
          )}
          {!loading && current?.type === "video" && (
            <div>
              <video data-testid="media-current-video" src={`${BACKEND}${current.url}`} controls autoPlay loop className="w-full rounded-xl border border-[#2A2625]/10 max-h-[520px] bg-black" />
              <div className="flex items-center justify-between mt-3">
                <p className="text-sm text-[#594F4D]">{current.prompt}</p>
                <button onClick={() => download(`${BACKEND}${current.url}`, current.video_id + ".mp4")} className="flex items-center gap-2 text-sm border border-[#2A2625]/15 hover:bg-[#2A2625]/5 rounded-full px-4 py-2 transition-colors"><DownloadSimple size={16} /> Download</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Galleries */}
      {tab === "video" ? (
        <div className="mt-8">
          <h3 className="font-display font-semibold mb-4">Your videos</h3>
          {videos.length === 0 ? (
            <div className="bg-white border border-dashed border-[#2A2625]/15 rounded-2xl p-12 text-center"><FilmSlate size={28} weight="duotone" className="text-[#F29A86] mx-auto" /><p className="text-[#594F4D] mt-3">No videos yet.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="video-gallery">
              {videos.filter((v) => v.status === "done").map((v) => (
                <div key={v.video_id} className="group relative rounded-xl overflow-hidden border border-[#2A2625]/10 bg-black" data-testid={`gallery-video-${v.video_id}`}>
                  <video src={`${BACKEND}${v.url}`} poster={v.thumb ? `${BACKEND}${v.thumb}` : undefined} controls className="w-full aspect-video object-cover" />
                  <button onClick={() => setLightbox({ type: "video", url: v.url, prompt: v.prompt })} className="absolute top-2 left-2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity" title="Expand"><FilmSlate size={14} /></button>
                  <button onClick={() => removeVid(v.video_id)} className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8">
          <h3 className="font-display font-semibold mb-4">Your gallery</h3>
          {gallery.length === 0 ? (
            <div className="bg-white border border-dashed border-[#2A2625]/15 rounded-2xl p-12 text-center"><Sparkle size={28} weight="duotone" className="text-[#F29A86] mx-auto" /><p className="text-[#594F4D] mt-3">No images yet.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="media-gallery">
              {gallery.map((g) => (
                <div key={g.media_id} onClick={() => setLightbox({ type: "image", url: g.url, prompt: g.prompt })} className="group relative rounded-xl overflow-hidden border border-[#2A2625]/10 bg-white cursor-pointer" data-testid={`gallery-image-${g.media_id}`}>
                  <img src={`${BACKEND}${g.url}`} alt={g.prompt} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A2625]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                    <p className="text-white text-xs line-clamp-2">{g.prompt}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={(e) => { e.stopPropagation(); download(`${BACKEND}${g.url}`, g.media_id + ".png"); }} className="text-white/90 hover:text-white"><DownloadSimple size={16} /></button>
                      <button onClick={(e) => { e.stopPropagation(); removeImg(g.media_id); }} className="text-white/90 hover:text-[#E09494]"><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-[#2A2625]/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="lightbox">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "image"
              ? <img src={`${BACKEND}${lightbox.url}`} alt={lightbox.prompt} className="w-full max-h-[80vh] object-contain rounded-xl" />
              : <video src={`${BACKEND}${lightbox.url}`} controls autoPlay loop className="w-full max-h-[80vh] rounded-xl bg-black" />}
            <div className="flex items-center justify-between mt-3">
              <p className="text-white/85 text-sm">{lightbox.prompt}</p>
              <div className="flex gap-2">
                <button onClick={() => download(`${BACKEND}${lightbox.url}`, "media")} className="text-white text-sm flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-full px-4 py-2 transition-colors"><DownloadSimple size={16} /> Download</button>
                <button onClick={() => setLightbox(null)} className="text-white text-sm bg-white/15 hover:bg-white/25 rounded-full px-4 py-2 transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
